import { useEffect } from "react";
import { getLogScale, getScale } from "../domain/pointDomain";
import { GraphUseCase } from "../useCase/graphUseCase";
import { lineToRelativePoints, pointsToLine } from "../domain/lineDomain";

export const GRAPH_SCALE_SPACE = 70;

// ヘルパー関数: スケール計算
function setupScales(graphUseCase, leftBottom, rightTop, xLabel, yLabel) {
  const [xScale, subXScale] = xLabel.isLog
    ? getLogScale(leftBottom.x, rightTop.x)
    : getScale(leftBottom.x, rightTop.x);
  const [yScale, subYScale] = yLabel.isLog
    ? getLogScale(leftBottom.y, rightTop.y)
    : getScale(leftBottom.y, rightTop.y);

  const xScalePx = xScale.map((scale) => ({
    value: scale.value,
    posPx: graphUseCase.pxFromParcent(graphUseCase.parcentFromPos(scale.pos, 0))
      .x,
  }));

  const yScalePx = yScale.map((scale) => ({
    value: scale.value,
    posPx: graphUseCase.pxFromParcent(graphUseCase.parcentFromPos(0, scale.pos))
      .y,
  }));

  const subXScalePx = subXScale.map((scale) => ({
    value: scale.value,
    posPx: graphUseCase.pxFromParcent(graphUseCase.parcentFromPos(scale.pos, 0))
      .x,
  }));

  const subYScalePx = subYScale.map((scale) => ({
    value: scale.value,
    posPx: graphUseCase.pxFromParcent(graphUseCase.parcentFromPos(0, scale.pos))
      .y,
  }));

  return { xScalePx, yScalePx, subXScalePx, subYScalePx };
}

// ヘルパー関数: 軸を描画
function drawAxes(ctx, scaleSpace, width, height) {
  const rightEdge = scaleSpace + width;
  ctx.beginPath();
  ctx.moveTo(scaleSpace, height);
  ctx.lineTo(rightEdge, height);
  ctx.moveTo(scaleSpace, 0);
  ctx.lineTo(scaleSpace, height);
  ctx.save();
  ctx.rotate(-Math.PI / 2);
  ctx.restore();
  ctx.moveTo(scaleSpace, 0);
  ctx.lineTo(rightEdge, 0);
  ctx.moveTo(rightEdge, 0);
  ctx.lineTo(rightEdge, height);
}

const superString = ["⁰", "¹", "²", "³", "⁴", "⁵", "⁶", "⁷", "⁸", "⁹"];

// 上付き数字への変換テーブル
const SUPERSCRIPT_MAP = {
  0: superString[0],
  1: superString[1],
  2: superString[2],
  3: superString[3],
  4: superString[4],
  5: superString[5],
  6: superString[6],
  7: superString[7],
  8: superString[8],
  9: superString[9],
  "-": "⁻",
  "+": "",
};

function toSuperscript(str) {
  return String(str)
    .split("")
    .map((ch) => SUPERSCRIPT_MAP[ch] ?? ch)
    .join("");
}

// value を a×10^n 形式の文字列に変換
// threshold: これ以上/以下で指数表記に切り替える閾値（省略可）
function formatScientific(value, { digits = 2, minExp = 4 } = {}) {
  if (!value) return "";
  if (value == 0) return "0";
  if(superString.some((s)=>value.includes(s))) return value
  const exp = Math.floor(Math.log10(Math.abs(value)));

  // 指数の絶対値が小さいときは普通の数値表記のままにする
  if (Math.abs(exp) < minExp) {
    return String(value);
  }

  const mantissa = value / Math.pow(10, exp);
  // 誤差で 10.00 になったりするのを丸めて調整
  const roundedMantissa = Number(mantissa.toFixed(digits));

  return `${roundedMantissa}×10${toSuperscript(exp)}`;
}

// ヘルパー関数: 目盛りを描画
function drawScales(
  ctx,
  xScalePx,
  yScalePx,
  subXScalePx,
  subYScalePx,
  scaleSpace,
  height,
  width,
) {
  const rightEdge = scaleSpace + width;

  xScalePx.forEach((x) => {
    ctx.moveTo(x.posPx, height);
    ctx.lineTo(x.posPx, height - 10);
    ctx.fillText(formatScientific(x.value), x.posPx, height + 40);
  });

  subXScalePx.forEach((x) => {
    ctx.moveTo(x.posPx, height);
    ctx.lineTo(x.posPx, height - 5);
  });

  xScalePx.forEach((x) => {
    ctx.moveTo(x.posPx, 0);
    ctx.lineTo(x.posPx, 10);
  });

  subXScalePx.forEach((x) => {
    ctx.moveTo(x.posPx, 0);
    ctx.lineTo(x.posPx, 5);
  });

  ctx.textAlign = "right";
  yScalePx.forEach((y) => {
    ctx.moveTo(scaleSpace, y.posPx);
    ctx.lineTo(scaleSpace + 10, y.posPx);
    ctx.fillText(formatScientific(y.value), scaleSpace-10, y.posPx);
  });


  subYScalePx.forEach((y) => {
    ctx.moveTo(scaleSpace, y.posPx);
    ctx.lineTo(scaleSpace + 5, y.posPx);
  });

  yScalePx.forEach((y) => {
    ctx.moveTo(rightEdge, y.posPx);
    ctx.lineTo(rightEdge - 10, y.posPx);
  });

  subYScalePx.forEach((y) => {
    ctx.moveTo(rightEdge, y.posPx);
    ctx.lineTo(rightEdge - 5, y.posPx);
  });
}

// ヘルパー関数: データポイントを描画
function drawDataPoints(ctx, datas, graphUseCase) {
  datas.forEach((data) => {
    ctx.fillStyle = "#" + data.color.padEnd(6, "0");
    data.points.forEach((p) => {
      if (!graphUseCase.isInsideCanvas(p.x, p.y)) return;

      const { x, y } = graphUseCase.posToPx(p.x, p.y);
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
    });
  });
}

// ヘルパー関数: 近似直線を描画
function drawLine(ctx, graphUseCase, points, data) {
  if (!points || points.length < 2) return;
  const pointAPx = graphUseCase.posToPx(points[0].x, points[0].y);
  const pointBPx = graphUseCase.posToPx(points[1].x, points[1].y);

  function liangBarsky(x0, y0, x1, y1, xmin, xmax, ymin, ymax) {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const p = [-dx, dx, -dy, dy];
    const q = [x0 - xmin, xmax - x0, y0 - ymin, ymax - y0];
    let u1 = 0;
    let u2 = 1;

    for (let i = 0; i < 4; i++) {
      if (p[i] === 0) {
        if (q[i] < 0) return null;
      } else {
        const t = q[i] / p[i];
        if (p[i] < 0) {
          u1 = Math.max(u1, t);
        } else {
          u2 = Math.min(u2, t);
        }
      }
    }

    if (u1 > u2) return null;

    const cx0 = x0 + u1 * dx;
    const cy0 = y0 + u1 * dy;
    const cx1 = x0 + u2 * dx;
    const cy1 = y0 + u2 * dy;
    return { x0: cx0, y0: cy0, x1: cx1, y1: cy1 };
  }

  const xmin = graphUseCase.scaleSpace;
  const xmax = graphUseCase.width + graphUseCase.scaleSpace;
  const ymin = 0;
  const ymax = graphUseCase.height;

  const clipped = liangBarsky(
    pointAPx.x,
    pointAPx.y,
    pointBPx.x,
    pointBPx.y,
    xmin,
    xmax,
    ymin,
    ymax,
  );

  if (!clipped) return;

  ctx.beginPath();
  ctx.strokeStyle = "#" + data.color.padEnd(6, "0");
  ctx.lineWidth = 2;
  ctx.moveTo(clipped.x0, clipped.y0);
  ctx.lineTo(clipped.x1, clipped.y1);
  ctx.stroke();
}

// カスタムフック: グラフ描画
export function useDrawGraph({
  canvasRef,
  leftBottom,
  rightTop,
  datas,
  xLabel,
  yLabel,
  isPrinting,
  height
}) {
  // 近似直線の計算を描画とは別の副作用に分離
  useEffect(() => {
    if (!Array.isArray(datas)) return;

    // 各 series に `line` プロパティを付与（破壊的代入）
    datas.forEach((data) => {
      data.line = pointsToLine(data.points, xLabel.isLog, yLabel.isLog);
    });
  }, [datas.flatMap((d) => d.points), xLabel.isLog, yLabel.isLog]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const graphUseCase = new GraphUseCase(
      cssWidth - GRAPH_SCALE_SPACE,
      cssHeight - GRAPH_SCALE_SPACE,
      GRAPH_SCALE_SPACE,
      rightTop,
      leftBottom,
      xLabel,
      yLabel,
    );

    const localWidth = cssWidth - GRAPH_SCALE_SPACE;
    const localHeight = cssHeight - GRAPH_SCALE_SPACE;

    const { xScalePx, yScalePx, subXScalePx, subYScalePx } = setupScales(
      graphUseCase,
      leftBottom,
      rightTop,
      xLabel,
      yLabel,
    );

    ctx.font = "18px Times New Roman";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    drawAxes(ctx, GRAPH_SCALE_SPACE, localWidth, localHeight);

    drawScales(
      ctx,
      xScalePx,
      yScalePx,
      subXScalePx,
      subYScalePx,
      GRAPH_SCALE_SPACE,
      localHeight,
      localWidth,
    );

    ctx.stroke();

    drawDataPoints(ctx, datas, graphUseCase, xLabel, yLabel);

    // 近似直線を描画（事前計算された `data.line` を優先して使用）
    datas.forEach((data) => {
      const line = data.line;
      if (!line) return;
      if (!data.isLine) return;

      const relatePoints = lineToRelativePoints(
        line,
        rightTop,
        leftBottom,
        xLabel,
        yLabel,
      );
      if (relatePoints) {
        drawLine(ctx, graphUseCase, relatePoints, data);
      }
    });
  }, [canvasRef, leftBottom, rightTop, datas, xLabel, yLabel, isPrinting,height]);
}
